import React, { useMemo, useState } from "react";
import { API_ORIGIN } from "../../services/api.config";
import Button from "../inputs/Button";
import Badge from "./Badge";
import Modal from "./Modal";
import { canReviewApplication } from "../../utils/applicationStatus";

// A single reusable modal capable of rendering either an application or a cadre member
// Use via:
// <CadreDetailsModal type="application" data={application} .../>
// <CadreDetailsModal type="cadre" data={cadre} .../>
const CadreDetailsModal = ({
  isOpen,
  onClose,
  type = "cadre",
  data,
  // Application actions
  onApprove,
  onReject,
  onDelete,
  // Cadre select action
  onSelect,
  showSelectButton = false,
  loading = false,
}) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Backwards compatibility if older props are passed
  const entity = useMemo(() => {
    if (data) return data;
    // legacy fallbacks
    // eslint-disable-next-line no-unused-vars
    const legacy = {};
    return data;
  }, [data]);

  if (!isOpen || !entity) return null;

  const isApplication = type === "application";
  const isCadre = type === "cadre";

  const cadreProfile = isCadre ? entity.cadreProfile || entity : null;

  // helpers

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case "AVAILABLE":
        return "bg-green-100 text-green-800";
      case "LIMITED":
        return "bg-yellow-100 text-yellow-800";
      case "UNAVAILABLE":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const normalizeToArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  const formatArray = (arr) => {
    if (!arr) return "N/A";
    if (Array.isArray(arr)) {
      return arr.length > 0 ? arr.join(", ") : "N/A";
    }
    return arr;
  };

  const employmentLabel = isApplication
    ? entity?.employmentType || entity?.availability || null
    : cadreProfile?.availability || null;

  const handleApprove = () => {
    if (onApprove && isApplication) {
      onApprove(entity.id);
    }
  };

  const handleReject = () => {
    if (!isApplication) return;
    if (showRejectionForm) {
      if (rejectionReason.trim()) {
        if (onReject) {
          onReject(entity.id, rejectionReason);
        }
        setRejectionReason("");
        setShowRejectionForm(false);
      }
    } else {
      setShowDeleteConfirm(false);
      setShowRejectionForm(true);
    }
  };

  const handleCancelReject = () => {
    setShowRejectionForm(false);
    setRejectionReason("");
  };

  const handleDelete = () => {
    if (!isApplication || !onDelete) return;
    if (showDeleteConfirm) {
      onDelete(entity.id);
      setShowDeleteConfirm(false);
    } else {
      setShowRejectionForm(false);
      setShowDeleteConfirm(true);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const canReview = isApplication && canReviewApplication(entity.status);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="xl"
      className="max-w-6xl"
    >
      <div className="bg-white">
        {/* Header */}
        <div className="rounded-t-lg bg-gradient-to-r from-custom-blue to-custom-light-blue p-6 mb-6 text-white">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold">
                {isApplication
                  ? entity.applicantName || "Candidate Name"
                  : entity.user?.name || entity.name || "Unknown Member"}
              </h2>
              <div className="flex items-center space-x-4 mt-1 opacity-90">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2H8a2 2 0 00-2 2v2"
                    />
                  </svg>
                  <span className="text-sm">
                    {(isApplication
                      ? entity.yearsExperience
                      : cadreProfile?.yearsExperience ||
                        entity.yearsExperience) || 0}{" "}
                    yrs exp
                  </span>
                </div>
                {employmentLabel && (
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-sm">{employmentLabel}</span>
                  </div>
                )}
                {isCadre && (
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${getAvailabilityColor(
                        cadreProfile?.availability
                      )}`}
                    >
                      {cadreProfile?.availability || "AVAILABLE"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative">
          <div className="hidden lg:block absolute left-1/3 top-0 bottom-0 w-px bg-gray-200 transform -translate-x-1/2"></div>

          {/* Left Column - Contact and COI */}
          <div className="space-y-6 lg:col-span-1">
            <div className="space-y-3 bg-gray-50 rounded-lg p-5 border border-gray-100">
              {/* Email */}
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <div>
                  <span className="text-sm font-medium text-gray-500">
                    Email
                  </span>
                  <div>
                    <a
                      href={`mailto:${
                        isApplication
                          ? entity.email
                          : entity.user?.email || entity.email
                      }`}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {isApplication
                        ? entity.email
                        : entity.user?.email || entity.email || "N/A"}
                    </a>
                  </div>
                </div>
              </div>
              {/* Phone */}
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <div>
                  <span className="text-sm font-medium text-gray-500">
                    Phone
                  </span>
                  <div className="text-gray-900">
                    {(isApplication
                      ? entity.phone
                      : entity.user?.phone || entity.phone) || "N/A"}
                  </div>
                </div>
              </div>
              {/* Education */}
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 14l9-5-9-5-9 5 9 5z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                  />
                </svg>
                <div>
                  <span className="text-sm font-medium text-gray-500">
                    Education
                  </span>
                  <div className="text-gray-900">
                    {(isApplication
                      ? entity.education
                      : cadreProfile?.education) || "N/A"}
                  </div>
                </div>
              </div>
              {/* Resume (only if present) */}
              {(isApplication ? entity.resume : cadreProfile?.resume) && (
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Resume
                    </span>
                    <div>
                      <a
                        href={(() => {
                          const resume = isApplication
                            ? entity.resume
                            : cadreProfile?.resume;
                          if (!resume) return "#";
                          if (resume.startsWith("/uploads/")) {
                            return `${API_ORIGIN}${resume}`;
                          }
                          return resume;
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        View Resume
                        <svg
                          className="w-4 h-4 inline ml-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Application COI details if present */}
              {isApplication &&
                entity.conflictOfInterest &&
                Object.keys(entity.conflictOfInterest).length > 0 && (
                  <div className="border-b border-gray-200 mt-3"></div>
                )}
              {isApplication &&
                entity.conflictOfInterest &&
                Object.keys(entity.conflictOfInterest).length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 mr-2 rounded-full bg-red-100 text-red-700">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </span>
                      Conflict of Interest Declaration
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          1. Current or past employee of FEMA or DHS?
                        </p>
                        <p className="text-gray-900 font-medium">
                          {entity.conflictOfInterest.employeeOfFemaOrDhs ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          2. Retired employee of FEMA or DHS?
                        </p>
                        <p className="text-gray-900 font-medium">
                          {entity.conflictOfInterest
                            .retiredEmployeeOfFemaOrDhs || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          3. Current employee or contractor of FEMA/PTS?
                        </p>
                        <p className="text-gray-900 font-medium">
                          {entity.conflictOfInterest
                            .currentEmployeeOrContractorOfFEMA_PTS || "N/A"}
                        </p>
                        {entity.conflictOfInterest
                          .currentEmployeeOrContractorOfFEMA_PTS === "YES" && (
                          <div className="mt-3 ml-4 space-y-2">
                            {entity.conflictOfInterest.ptsJointVenture && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">
                                  PTS Joint Venture:
                                </p>
                                <p className="text-gray-900">
                                  {entity.conflictOfInterest.ptsJointVenture}
                                </p>
                              </div>
                            )}
                            {entity.conflictOfInterest.currentWorkingLevel && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">
                                  Work Level:
                                </p>
                                <p className="text-gray-900">
                                  {
                                    entity.conflictOfInterest
                                      .currentWorkingLevel
                                  }
                                </p>
                              </div>
                            )}
                            {entity.conflictOfInterest.regionalRegions &&
                              entity.conflictOfInterest.regionalRegions.length >
                                0 && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500">
                                    Regions:
                                  </p>
                                  <p className="text-gray-900">
                                    {entity.conflictOfInterest.regionalRegions.join(
                                      ", "
                                    )}
                                  </p>
                                </div>
                              )}
                            {entity.conflictOfInterest.workingStates &&
                              entity.conflictOfInterest.workingStates.length >
                                0 && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500">
                                    States:
                                  </p>
                                  <p className="text-gray-900">
                                    {entity.conflictOfInterest.workingStates.join(
                                      ", "
                                    )}
                                  </p>
                                </div>
                              )}
                            {entity.conflictOfInterest.communityName && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">
                                  Local Community Name:
                                </p>
                                <p className="text-gray-900">
                                  {entity.conflictOfInterest.communityName}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          4. Past employee or contractor of FEMA/PTS?
                        </p>
                        <p className="text-gray-900 font-medium">
                          {entity.conflictOfInterest.pastEmployeeOrContractor ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          5. NFIP related flood mapping study involvement?
                        </p>
                        <p className="text-gray-900 font-medium">
                          {entity.conflictOfInterest.nfipInvolvement || "N/A"}
                        </p>
                        {entity.conflictOfInterest.nfipInvolvement ===
                          "YES" && (
                          <div className="mt-3 ml-4 space-y-2">
                            {entity.conflictOfInterest.nfipWorkingLevel && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">
                                  NFIP Work Level:
                                </p>
                                <p className="text-gray-900">
                                  {entity.conflictOfInterest.nfipWorkingLevel}
                                </p>
                              </div>
                            )}
                            {entity.conflictOfInterest
                              .nfipInvolvementDescription && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">
                                  NFIP Details:
                                </p>
                                <div className="mt-1 p-3 bg-white rounded border text-gray-900 whitespace-pre-wrap">
                                  {
                                    entity.conflictOfInterest
                                      .nfipInvolvementDescription
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>{" "}
            {entity.conflictOfInterestDetail && (
              <div className="space-y-6 lg:col-span-2">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-500">
                      Conflict of Interest
                    </span>
                    {entity.conflictOfInterestDetail && (
                      <div className="mt-1 text-gray-800 whitespace-pre-wrap">
                        {entity.conflictOfInterestDetail.evaluatedStatus}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Professional Information and Actions */}
          <div className="space-y-6 lg:col-span-2">
            {/* Professional Information */}
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                <span className="inline-flex items-center justify-center w-6 h-6 mr-2 rounded-full bg-purple-100 text-purple-700">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 14l9-5-9-5-9 5 9 5z"
                    />
                  </svg>
                </span>
                Professional Information
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Expertise Areas
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(() => {
                      const expertise = isApplication
                        ? entity.expertise
                        : cadreProfile?.expertise;
                      const arr = Array.isArray(expertise)
                        ? expertise
                        : normalizeToArray(expertise);
                      return arr.length > 0 ? (
                        arr.map((area, index) => (
                          <Badge key={index} variant="primary" size="sm">
                            {area}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="primary" size="sm">
                          {formatArray(expertise)}
                        </Badge>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Certifications
                  </p>
                  <p className="text-gray-900 mt-1">
                    {(isApplication
                      ? entity.certifications
                      : normalizeToArray(cadreProfile?.certifications).join(
                          ", "
                        ) || "") || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Short Bio / Cover Letter */}
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                <span className="inline-flex items-center justify-center w-6 h-6 mr-2 rounded-full bg-green-100 text-green-700">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16h6M4 6h16v12H4z"
                    />
                  </svg>
                </span>
                {isApplication ? "Short Bio" : "Bio"}
              </h3>
              <div className="mt-1 p-3 bg-white border border-gray-200 rounded-md min-h-32">
                <p className="text-gray-900 whitespace-pre-wrap">
                  {(isApplication ? entity.coverLetter : cadreProfile?.bio) ||
                    (isApplication ? "Short Bio *" : "No bio provided")}
                </p>
              </div>
            </div>

            {/* Optional Notes if present on application */}
            {isApplication && entity.notes && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Notes
                </h3>
                <div className="bg-gray-100 rounded-md p-3">
                  <p className="text-sm text-gray-700">
                    {entity.notes || "No notes provided"}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              {isApplication && onDelete && (
                <Button
                  onClick={handleDelete}
                  variant="danger"
                  size="sm"
                  disabled={loading}
                >
                  Delete
                </Button>
              )}
              {canReview && (
                <>
                  <Button
                    onClick={handleReject}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={handleApprove}
                    variant="primary"
                    size="sm"
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Approve"}
                  </Button>
                </>
              )}
              {isCadre && showSelectButton && onSelect && (
                <Button
                  onClick={onSelect}
                  variant="primary"
                  size="sm"
                  disabled={loading}
                >
                  {loading ? "Selecting..." : "Select Panelist"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Rejection Reason Form */}
        {isApplication && showDeleteConfirm && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-red-800 mb-2">
              Delete this application?
            </h4>
            <p className="text-sm text-red-800 mb-3">
              This permanently removes the application. This cannot be undone.
            </p>
            <div className="flex space-x-2">
              <Button
                onClick={handleDelete}
                variant="danger"
                size="sm"
                disabled={loading}
              >
                {loading ? "Deleting..." : "Confirm delete"}
              </Button>
              <Button onClick={handleCancelDelete} variant="outline" size="sm">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isApplication && showRejectionForm && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-red-800 mb-2">
              Rejection Reason
            </h4>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              className="w-full p-3 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
              rows={3}
            />
            <div className="mt-3 flex space-x-2">
              <Button
                onClick={handleReject}
                variant="danger"
                size="sm"
                disabled={!rejectionReason.trim() || loading}
              >
                Confirm Rejection
              </Button>
              <Button onClick={handleCancelReject} variant="outline" size="sm">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CadreDetailsModal;
